import { Component, effect, Input, type OnChanges, type OnDestroy, type OnInit, type SimpleChanges, ViewChild, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { type Chart, type ChartConfiguration, type ChartType, type Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ThemeService } from '../../services/theme.service';
import { applyChartThemeDefaults, applyChartThemeToChart, CHART_DATA } from '../../utilities/chart-theme.util';

const DECIMAL_BASE = 10;
const TEMPERATURE_DECIMALS = 1;
const HUMIDITY_DECIMALS = 0;
const THI_DECIMALS = 1;
const MIDPOINT_DIVISOR = 2;
const HOURS_PER_DAY = 24;
const LAST_HOUR_INDEX = HOURS_PER_DAY - 1;
const HOUR_PAD_LENGTH = 2;
const INTERVAL_MIDPOINT_OFFSET = 0.5;
const HOUR_GRID_LINE_WIDTH = 1;
const MINUTES_PER_HOUR = 60;
const SUMMARY_REFRESH_INTERVAL_MS = 300_000;

const THI_NO_STRESS_MAX = 68;
const THI_MILD_STRESS_MAX = 72;
const THI_MODERATE_STRESS_MAX = 80;
const THI_SEVERE_STRESS_MAX = 84;

const THI_NO_STRESS_SAMPLE = 67;
const THI_MILD_STRESS_SAMPLE = 70;
const THI_MODERATE_STRESS_SAMPLE = 76;
const THI_SEVERE_STRESS_SAMPLE = 82;
const THI_VERY_SEVERE_STRESS_SAMPLE = 86;

interface ThiLegendItem {
  labelKey: string;
  color: string;
  borderColor: string;
}

@Component({
  selector: 'app-weather-chart',
  templateUrl: './weather-chart.component.html',
  styleUrl: './weather-chart.component.scss',
  standalone: false
})
export class WeatherChartComponent implements OnChanges, OnInit, OnDestroy {
  private summaryRefreshTimer: ReturnType<typeof setInterval> | null = null;
  @Input() title = 'Weather';
  @Input() labels: string[] = [];
  @Input() temperatureData: number[] = [];
  @Input() humidityData: number[] = [];
  @Input() thiData: number[] = [];
  @Input() precipitationProbabilityData: number[] = [];
  @Input() precipitationOffsetPx = 50;
  @Input() precipitationRightOffsetPx = 50;
  @Input() precipitationBarWidthPercent = 100;
  @Input() precipitationStripWidthPercent = 100;
  @Input() location = '';
  @Input() isLoading = false;
  @Input() updatedAt?: Date | null;
  /**
   * When true, the panel can be collapsed to just its header (title + the at-a-glance
   * temperature / humidity / THI / rain summary), hiding the precipitation strip, THI
   * legend, and hourly chart. Used by the dashboard's top "today's conditions" banner so
   * the heavy chart only renders on demand. When false the panel is always fully expanded
   * (its original behaviour).
   */
  @Input() collapsible = false;
  /** Current expand/collapse state. Honoured only when {@link collapsible} is true. */
  @Input() expanded = true;

  private translate = inject(TranslateService);

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  private readonly themeService = inject(ThemeService);

  public lineChartType: ChartType = 'bar';
  public lineChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: []
  };

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          filter: (legendItem, chartData) => {
            const datasetIndex = legendItem.datasetIndex;
            if (datasetIndex === undefined || datasetIndex < 0) {
              return true;
            }

            const dataset = chartData.datasets?.[datasetIndex];
            const thiLabel = this.translate.instant('DASHBOARD.WEATHER_THI');
            return !(dataset?.type === 'bar' && dataset?.label === thiLabel);
          }
        }
      },
      tooltip: {
        callbacks: {
          title: contexts => {
            const x = contexts[0]?.parsed?.x;
            if (x === undefined || x === null) {
              return '';
            }
            if (Number.isInteger(x)) {
              return `${String(x).padStart(HOUR_PAD_LENGTH, '0')}:00`;
            }
            const startH = Math.floor(x);
            const endH = startH + 1;
            return `${String(startH).padStart(HOUR_PAD_LENGTH, '0')}:00 – ${String(endH).padStart(HOUR_PAD_LENGTH, '0')}:00`;
          },
          label: context => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (value === null || value === undefined) {
              return `${label}: --`;
            }
            if (label.toLowerCase().includes('humidity')) {
              return `${label}: ${formatValue(value, HUMIDITY_DECIMALS)}%`;
            }
            if (label.toLowerCase().includes('thi')) {
              const thiBandLabel = this.translate.instant(getThiBandLabelKey(value));
              return `${label}: ${formatValue(value, THI_DECIMALS)} (${thiBandLabel})`;
            }
            return `${label}: ${formatValue(value, TEMPERATURE_DECIMALS)}°C`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear' as const,
        min: 0,
        max: LAST_HOUR_INDEX,
        offset: false,
        ticks: {
          stepSize: 1,
          callback: (value: number | string) => {
            const h = Number(value);
            return Number.isInteger(h) ? `${String(h).padStart(HOUR_PAD_LENGTH, '0')}:00` : '';
          }
        },
        title: {
          display: true,
          text: 'Hour'
        }
      },
      yTemp: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Temperature (°C)'
        }
      },
      yHumidity: {
        type: 'linear',
        position: 'right',
        title: {
          display: true,
          text: 'Humidity (%)'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  temperatureSummary: { current: number | null; min: number | null; max: number | null } = {
    current: null,
    min: null,
    max: null
  };

  humiditySummary: { current: number | null; min: number | null; max: number | null } = {
    current: null,
    min: null,
    max: null
  };

  thiSummary: { current: number | null; min: number | null; max: number | null } = {
    current: null,
    min: null,
    max: null
  };

  precipitationSummary: { maxProbability: number | null } = {
    maxProbability: null
  };

  public hourGridPlugin: Plugin = {
    id: 'hourGrid',
    beforeDatasetsDraw: (chart: Chart) => {
      const xScale = chart.scales['x'];
      if (!xScale) {
        return;
      }
      const { ctx } = chart;
      const { top, bottom } = chart.chartArea;
      ctx.save();
      // Theme-aware hour gridlines (this custom canvas plugin can't read the generic chart theme).
      ctx.strokeStyle = this.themeService.isDark() ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
      ctx.lineWidth = HOUR_GRID_LINE_WIDTH;
      for (let h = 0; h < HOURS_PER_DAY; h++) {
        const x = xScale.getPixelForValue(h);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
      }
      ctx.restore();
    }
  };

  precipitationStrip: { index: number; probability: number; opacity: number; label: string; endLabel: string }[] = [];
  thiLegend: ThiLegendItem[] = getThiLegendItems();
  precipitationGridTemplate = '';
  precipitationPaddingLeft = 0;
  precipitationPaddingRight = 0;

  constructor() {
    // Re-theme on light/dark change. Ticks/legend/titles/grid all use chart.js defaults here,
    // so setting the global defaults + updating is enough. Data-line colours stay fixed.
    effect(() => {
      const isDark = this.themeService.isDark();
      applyChartThemeDefaults(isDark);
      applyChartThemeToChart(this.chart?.chart, isDark);
      this.chart?.update();
    });
  }

  ngOnInit(): void {
    this.summaryRefreshTimer = setInterval(() => this.updateSummaries(), SUMMARY_REFRESH_INTERVAL_MS);
  }

  ngOnDestroy(): void {
    if (this.summaryRefreshTimer !== null) {
      clearInterval(this.summaryRefreshTimer);
    }
  }

  /**
   * Toggle the collapsed/expanded state. No-op when {@link collapsible} is false.
   * On expand the chart canvas mounts fresh, so we re-align the precipitation strip to
   * the chart's plot area once that area becomes measurable (see {@link alignPrecipitationWhenReady}).
   */
  toggleExpanded(): void {
    if (!this.collapsible) {
      return;
    }
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.alignPrecipitationWhenReady();
    }
  }

  /**
   * After an expand, chart.js needs one or more animation frames to lay out the freshly
   * mounted canvas before {@code chartArea} is populated. We poll with requestAnimationFrame
   * until the plot area has a non-zero width, then align the precipitation strip to it — the
   * strip's per-hour columns only line up with the chart when its left/right padding matches
   * the chart's plot area. requestAnimationFrame is zone-patched, so updating the padding
   * properties here still triggers change detection.
   */
  private alignPrecipitationWhenReady(retries = 10): void {
    requestAnimationFrame(() => {
      const area = this.chart?.chart?.chartArea;
      if (area && area.right > area.left) {
        this.updatePrecipitationAlignment();
      } else if (retries > 0) {
        this.alignPrecipitationWhenReady(retries - 1);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labels'] || changes['temperatureData'] || changes['humidityData'] || changes['thiData']) {
      const THI_BAR_BORDER_RADIUS = 3;
      const THI_BAR_BORDER_WIDTH = 1;

      // Compute the at-a-glance header summary FIRST, before building the Chart.js datasets.
      // The collapsed panel renders only this summary, so it must not be coupled to chart setup —
      // if any of the dataset/scale wiring below were to throw, the header would otherwise be left
      // blank even though the underlying weather data is perfectly valid.
      this.updateSummaries();

      this.lineChartData.labels = [];
      this.lineChartData.datasets = [
        {
          type: 'line',
          label: this.translate.instant('DASHBOARD.WEATHER_TEMPERATURE'),
          data: this.temperatureData.map((y, i) => ({ x: i, y })),
          borderColor: CHART_DATA.temp,
          backgroundColor: CHART_DATA.tempFill,
          pointRadius: 2,
          tension: 0.3,
          order: 1,
          yAxisID: 'yTemp'
        },
        {
          type: 'line',
          label: this.translate.instant('DASHBOARD.WEATHER_HUMIDITY'),
          data: this.humidityData.map((y, i) => ({ x: i, y })),
          borderColor: CHART_DATA.humidity,
          backgroundColor: CHART_DATA.humidityFill,
          pointRadius: 2,
          tension: 0.3,
          order: 1,
          yAxisID: 'yHumidity'
        },
        {
          type: 'bar',
          label: this.translate.instant('DASHBOARD.WEATHER_THI'),
          data: this.thiData.map((y, i) => ({ x: i + INTERVAL_MIDPOINT_OFFSET, y })),
          backgroundColor: this.thiData.map(value => getThiBandColor(value)),
          borderColor: this.thiData.map(value => getThiBandBorderColor(value)),
          borderRadius: THI_BAR_BORDER_RADIUS,
          borderWidth: THI_BAR_BORDER_WIDTH,
          barThickness: 'flex',
          barPercentage: 0.9,
          order: 2,
          yAxisID: 'yTemp'
        }
      ];

      if (this.lineChartOptions?.scales?.['x'] && 'title' in this.lineChartOptions.scales['x']) {
        this.setScaleTitle('x', this.translate.instant('DASHBOARD.WEATHER_HOUR'));
      }
      if (this.lineChartOptions?.scales?.['yTemp'] && 'title' in this.lineChartOptions.scales['yTemp']) {
        this.setScaleTitle('yTemp', this.translate.instant('DASHBOARD.WEATHER_TEMPERATURE_AXIS'));
      }
      if (this.lineChartOptions?.scales?.['yHumidity'] && 'title' in this.lineChartOptions.scales['yHumidity']) {
        this.setScaleTitle('yHumidity', this.translate.instant('DASHBOARD.WEATHER_HUMIDITY_AXIS'));
      }

      this.chart?.update();
      this.updatePrecipitationAlignment();
    }

    if (changes['precipitationProbabilityData'] || changes['labels']) {
      this.updatePrecipitationStrip();
    }
  }

  private updateSummaries(): void {
    this.temperatureSummary = {
      current: this.getInterpolatedValue(this.temperatureData),
      min: this.getMinValue(this.temperatureData),
      max: this.getMaxValue(this.temperatureData)
    };

    this.humiditySummary = {
      current: this.getInterpolatedValue(this.humidityData),
      min: this.getMinValue(this.humidityData),
      max: this.getMaxValue(this.humidityData)
    };

    this.thiSummary = {
      current: this.getCurrentHourValue(this.thiData),
      min: this.getMinValue(this.thiData),
      max: this.getMaxValue(this.thiData)
    };

    this.precipitationSummary = {
      maxProbability: this.getMaxValue(this.precipitationProbabilityData)
    };
  }

  private updatePrecipitationStrip(): void {
    const MIN_OPACITY = 0.15;
    const MAX_OPACITY = 0.9;
    const PROBABILITY_MAX = 100;
    const labels = this.labels || [];
    const probabilities = this.precipitationProbabilityData || [];
    const barCount = Math.max(labels.length - 1, 0);

    this.precipitationStrip = Array.from({ length: barCount }, (_, index) => {
      const label = labels[index] ?? '';
      const endLabel = labels[index + 1] ?? '';
      const startProbability = probabilities[index] ?? 0;
      const endProbability = probabilities[index + 1] ?? startProbability;
      const probability = (startProbability + endProbability) / MIDPOINT_DIVISOR;
      const intensity = Math.min(Math.max(probability / PROBABILITY_MAX, 0), 1);
      const opacity = MIN_OPACITY + (MAX_OPACITY - MIN_OPACITY) * intensity;

      return {
        index,
        probability,
        opacity,
        label,
        endLabel
      };
    });

    this.precipitationGridTemplate = barCount > 0 ? `repeat(${barCount}, minmax(0, 1fr))` : '';
    this.updatePrecipitationAlignment();
  }

  private updatePrecipitationAlignment(): void {
    const chart = this.chart?.chart;
    if (!chart || !chart.chartArea) {
      return;
    }

    const ratio = chart.currentDevicePixelRatio || 1;
    const leftPadding = chart.chartArea.left / ratio;
    const rightPadding = (chart.width - chart.chartArea.right) / ratio;

    this.precipitationPaddingLeft = Math.max(leftPadding, 0);
    this.precipitationPaddingRight = Math.max(rightPadding, 0);
  }

  private getInterpolatedValue(data: number[]): number | null {
    if (data.length === 0) {
      return null;
    }
    const now = new Date();
    const h = now.getHours();
    const index = Math.min(h, data.length - 1);
    const value = data[index];
    if (value === undefined) {
      return null;
    }
    const nextValue = data[index + 1];
    if (nextValue === undefined) {
      return value;
    }
    const fraction = now.getMinutes() / MINUTES_PER_HOUR;
    return value + (nextValue - value) * fraction;
  }

  private getCurrentHourValue(data: number[]): number | null {
    if (data.length === 0) {
      return null;
    }
    const index = Math.min(new Date().getHours(), data.length - 1);
    return data[index] ?? null;
  }

  private getMinValue(data: number[]): number | null {
    return data.length > 0 ? Math.min(...data) : null;
  }

  private getMaxValue(data: number[]): number | null {
    return data.length > 0 ? Math.max(...data) : null;
  }

  private setScaleTitle(scaleKey: 'x' | 'yTemp' | 'yHumidity', text: string): void {
    const scales = this.lineChartOptions?.scales;
    if (!scales) {
      return;
    }

    const scale = scales[scaleKey];
    if (!scale || typeof scale !== 'object' || !('title' in scale)) {
      return;
    }

    const titleCandidate = scale.title;
    if (!titleCandidate || typeof titleCandidate !== 'object') {
      return;
    }

    (titleCandidate as { text?: string }).text = text;
  }

  get precipitationBarWidth(): number {
    const MIN_WIDTH = 10;
    const MAX_WIDTH = 100;
    return Math.min(Math.max(this.precipitationBarWidthPercent, MIN_WIDTH), MAX_WIDTH);
  }

  get precipitationStripWidth(): number {
    const MIN_WIDTH = 20;
    const MAX_WIDTH = 100;
    return Math.min(Math.max(this.precipitationStripWidthPercent, MIN_WIDTH), MAX_WIDTH);
  }
}

function formatValue(value: number, decimals: number): string {
  const multiplier = Math.pow(DECIMAL_BASE, decimals);
  return (Math.round(value * multiplier) / multiplier).toFixed(decimals);
}

function getThiBandLabelKey(thi: number): string {
  if (thi < THI_NO_STRESS_MAX) {
    return 'DASHBOARD.WEATHER_THI_NO_HEAT_STRESS';
  }
  if (thi < THI_MILD_STRESS_MAX) {
    return 'DASHBOARD.WEATHER_THI_MILD_STRESS';
  }
  if (thi < THI_MODERATE_STRESS_MAX) {
    return 'DASHBOARD.WEATHER_THI_MODERATE_STRESS';
  }
  if (thi < THI_SEVERE_STRESS_MAX) {
    return 'DASHBOARD.WEATHER_THI_SEVERE_STRESS';
  }
  return 'DASHBOARD.WEATHER_THI_VERY_SEVERE_STRESS';
}

function getThiBandColor(thi: number): string {
  if (thi < THI_NO_STRESS_MAX) {
    return CHART_DATA.thiFill.none;
  }
  if (thi < THI_MILD_STRESS_MAX) {
    return CHART_DATA.thiFill.mild;
  }
  if (thi < THI_MODERATE_STRESS_MAX) {
    return CHART_DATA.thiFill.moderate;
  }
  if (thi < THI_SEVERE_STRESS_MAX) {
    return CHART_DATA.thiFill.severe;
  }
  return CHART_DATA.thiFill.extreme;
}

function getThiBandBorderColor(thi: number): string {
  if (thi < THI_NO_STRESS_MAX) {
    return CHART_DATA.thi.none;
  }
  if (thi < THI_MILD_STRESS_MAX) {
    return CHART_DATA.thi.mild;
  }
  if (thi < THI_MODERATE_STRESS_MAX) {
    return CHART_DATA.thi.moderate;
  }
  if (thi < THI_SEVERE_STRESS_MAX) {
    return CHART_DATA.thi.severe;
  }
  return CHART_DATA.thi.extreme;
}

function getThiLegendItems(): ThiLegendItem[] {
  return [
    {
      labelKey: 'DASHBOARD.WEATHER_THI_NO_HEAT_STRESS',
      color: getThiBandColor(THI_NO_STRESS_SAMPLE),
      borderColor: getThiBandBorderColor(THI_NO_STRESS_SAMPLE)
    },
    {
      labelKey: 'DASHBOARD.WEATHER_THI_MILD_STRESS',
      color: getThiBandColor(THI_MILD_STRESS_SAMPLE),
      borderColor: getThiBandBorderColor(THI_MILD_STRESS_SAMPLE)
    },
    {
      labelKey: 'DASHBOARD.WEATHER_THI_MODERATE_STRESS',
      color: getThiBandColor(THI_MODERATE_STRESS_SAMPLE),
      borderColor: getThiBandBorderColor(THI_MODERATE_STRESS_SAMPLE)
    },
    {
      labelKey: 'DASHBOARD.WEATHER_THI_SEVERE_STRESS',
      color: getThiBandColor(THI_SEVERE_STRESS_SAMPLE),
      borderColor: getThiBandBorderColor(THI_SEVERE_STRESS_SAMPLE)
    },
    {
      labelKey: 'DASHBOARD.WEATHER_THI_VERY_SEVERE_STRESS',
      color: getThiBandColor(THI_VERY_SEVERE_STRESS_SAMPLE),
      borderColor: getThiBandBorderColor(THI_VERY_SEVERE_STRESS_SAMPLE)
    }
  ];
}
